import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertTriangle, Receipt, Search, RefreshCw, DollarSign, TrendingUp, ShoppingBag, Users } from "lucide-react";

interface AddonTransaction {
  id: number;
  userId: number;
  userEmail: string;
  userFirstName: string | null;
  userLastName: string | null;
  addonDetails: {
    sku: string;
    type: string;
    displayName: string;
  } | null;
  quantity: number;
  unitsGranted: number | null;
  amountPaid: string;
  currency: string;
  status: 'succeeded' | 'failed' | 'refunded';
  source: string;
  createdAt: string;
}

interface TransactionAnalytics {
  revenue: {
    total: number;
    last30Days: number;
    currency: string;
  };
  transactions: {
    total: number;
    byStatus: {
      succeeded: number;
      failed: number;
      refunded: number;
    };
    last30Days: number;
  };
  users: {
    total: number;
    withPurchases: number;
    activeWithBalances: number;
    conversionRate: number;
  };
  popularAddons: Array<{
    addonId: number;
    addon: {
      sku: string;
      type: string;
      displayName: string;
    } | null;
    totalPurchases: number;
  }>;
  catalog: {
    totalAddons: number;
    activeAddons: number;
  };
}

interface AdminAddonTransactionsProps {
  onSelectUser?: (userId: number) => void;
}

export default function AdminAddonTransactions({ onSelectUser }: AdminAddonTransactionsProps) {
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [currentPage, setCurrentPage] = useState(0);
  const [showAnalytics, setShowAnalytics] = useState(true);
  const pageSize = 25;

  // Construct URL with query parameters for transactions
  const buildTransactionsUrl = () => {
    const params: string[] = [];
    if (statusFilter) params.push(`status=${encodeURIComponent(statusFilter)}`);
    if (search && !isNaN(Number(search))) params.push(`userId=${encodeURIComponent(search)}`);
    params.push(`limit=${pageSize}`);
    params.push(`offset=${currentPage * pageSize}`);
    return `/api/admin/addons/transactions?${params.join('&')}`;
  };

  // Fetch transactions with filtering
  const { data: transactionsResponse, isLoading: isLoadingTransactions, error: transactionsError, refetch: refetchTransactions } = useQuery<{
    success: boolean;
    data: {
      transactions: AddonTransaction[];
      pagination: {
        limit: number;
        offset: number;
        hasMore: boolean;
        total: number;
      };
    };
  }>({
    queryKey: [buildTransactionsUrl()],
    staleTime: 2 * 60 * 1000, // 2 minutes
  });

  // Fetch analytics data
  const { data: analyticsResponse, isLoading: isLoadingAnalytics, error: analyticsError } = useQuery<{
    success: boolean;
    data: TransactionAnalytics;
  }>({
    queryKey: ["/api/admin/addons/analytics"],
    staleTime: 5 * 60 * 1000, // 5 minutes
    enabled: showAnalytics,
  });

  const transactions = transactionsResponse?.data?.transactions || [];
  const pagination = transactionsResponse?.data?.pagination;
  const analytics = analyticsResponse?.data;

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "succeeded":
        return <Badge variant="default" className="bg-green-600">Succeeded</Badge>;
      case "failed":
        return <Badge variant="destructive">Failed</Badge>;
      case "refunded":
        return <Badge variant="outline" className="text-orange-600">Refunded</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  const getAddonTypeBadge = (type: string) => {
    switch (type) {
      case "illustrations":
        return <Badge variant="outline" className="text-purple-600">Illustrations</Badge>;
      case "storage":
        return <Badge variant="outline" className="text-teal-600">Storage</Badge>;
      case "unlimited_storage":
        return <Badge variant="default" className="bg-blue-600">Unlimited Storage</Badge>;
      default:
        return <Badge variant="secondary">{type}</Badge>;
    }
  };

  const formatCurrency = (amount: string, currency: string = 'usd') => {
    const value = parseFloat(amount);
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency.toUpperCase()
    }).format(value);
  };

  const handleSearchChange = (value: string) => {
    setSearch(value);
    setCurrentPage(0);
  };

  const handleStatusFilterChange = (value: string) => {
    setStatusFilter(value === "all" ? "" : value);
    setCurrentPage(0);
  };

  const isLoading = isLoadingTransactions || (showAnalytics && isLoadingAnalytics);
  const error = transactionsError || analyticsError;

  if (isLoading) {
    return (
      <Card data-testid="admin-addon-transactions">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Receipt className="h-5 w-5" />
            Add-on Transactions & Analytics
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="h-8 bg-gray-200 rounded animate-pulse"></div>
            <div className="h-64 bg-gray-200 rounded animate-pulse"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card data-testid="admin-addon-transactions">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Receipt className="h-5 w-5" />
            Add-on Transactions & Analytics
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center text-red-600 py-8">
            <AlertTriangle className="w-5 h-5 mr-2" />
            Error loading data: {error instanceof Error ? error.message : "Unknown error"}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-addon-transactions">
      {/* Analytics Overview */}
      {showAnalytics && analytics && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <TrendingUp className="h-5 w-5" />
                Analytics Overview
              </CardTitle>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowAnalytics(!showAnalytics)}
                data-testid="toggle-analytics-button"
              >
                Hide Analytics
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              {/* Revenue Stats */}
              <Card>
                <CardContent className="pt-4">
                  <div className="flex items-center gap-2">
                    <DollarSign className="h-4 w-4 text-green-600" />
                    <div>
                      <div className="text-2xl font-bold">
                        {formatCurrency(analytics.revenue.total.toString(), analytics.revenue.currency)}
                      </div>
                      <p className="text-sm text-muted-foreground">Total Revenue</p>
                      <p className="text-xs text-green-600">
                        +{formatCurrency(analytics.revenue.last30Days.toString(), analytics.revenue.currency)} last 30d
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Transactions Stats */}
              <Card>
                <CardContent className="pt-4">
                  <div className="flex items-center gap-2">
                    <Receipt className="h-4 w-4 text-blue-600" />
                    <div>
                      <div className="text-2xl font-bold">{analytics.transactions.total}</div>
                      <p className="text-sm text-muted-foreground">Total Transactions</p>
                      <p className="text-xs text-blue-600">
                        +{analytics.transactions.last30Days} last 30d
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* User Stats */}
              <Card>
                <CardContent className="pt-4">
                  <div className="flex items-center gap-2">
                    <Users className="h-4 w-4 text-purple-600" />
                    <div>
                      <div className="text-2xl font-bold">{analytics.users.withPurchases}</div>
                      <p className="text-sm text-muted-foreground">Users with Purchases</p>
                      <p className="text-xs text-purple-600">
                        {analytics.users.conversionRate}% conversion rate
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Success Rate */}
              <Card>
                <CardContent className="pt-4">
                  <div className="flex items-center gap-2">
                    <ShoppingBag className="h-4 w-4 text-teal-600" />
                    <div>
                      <div className="text-2xl font-bold">
                        {Math.round((analytics.transactions.byStatus.succeeded / analytics.transactions.total) * 100)}%
                      </div>
                      <p className="text-sm text-muted-foreground">Success Rate</p>
                      <p className="text-xs text-teal-600">
                        {analytics.transactions.byStatus.succeeded} succeeded
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Popular Add-ons */}
            {analytics.popularAddons.length > 0 && (
              <div>
                <h4 className="text-lg font-semibold mb-3">Popular Add-ons</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {analytics.popularAddons.slice(0, 6).map((addon, index) => (
                    <Card key={addon.addonId}>
                      <CardContent className="pt-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-medium text-sm">
                              {addon.addon?.displayName || `Addon ${addon.addonId}`}
                            </p>
                            {addon.addon && (
                              <div className="mt-1">
                                {getAddonTypeBadge(addon.addon.type)}
                              </div>
                            )}
                          </div>
                          <div className="text-right">
                            <div className="text-lg font-bold">{addon.totalPurchases}</div>
                            <p className="text-xs text-muted-foreground">purchases</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Transactions Table */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Receipt className="h-5 w-5" />
              Transaction History
            </CardTitle>
            <div className="flex gap-2">
              {!showAnalytics && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowAnalytics(true)}
                  data-testid="show-analytics-button"
                >
                  <TrendingUp className="h-4 w-4 mr-2" />
                  Show Analytics
                </Button>
              )}
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => refetchTransactions()}
                data-testid="refresh-transactions-button"
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                Refresh
              </Button>
            </div>
          </div>
          
          {/* Search and Filter Controls */}
          <div className="flex gap-4 mt-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <Input
                placeholder="Search by user ID, email..."
                value={search}
                onChange={(e) => handleSearchChange(e.target.value)}
                className="pl-10"
                data-testid="search-transactions-input"
              />
            </div>
            <Select value={statusFilter || "all"} onValueChange={handleStatusFilterChange}>
              <SelectTrigger className="w-48" data-testid="status-filter-select">
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="succeeded">Succeeded</SelectItem>
                <SelectItem value="failed">Failed</SelectItem>
                <SelectItem value="refunded">Refunded</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardHeader>

        <CardContent>
          {transactions.length === 0 ? (
            <div className="text-center text-muted-foreground py-8">
              {search || statusFilter ? "No transactions found matching your criteria." : "No transactions found."}
            </div>
          ) : (
            <div className="space-y-4">
              {/* Transactions Table */}
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left p-3">Transaction</th>
                      <th className="text-left p-3">User</th>
                      <th className="text-left p-3">Add-on</th>
                      <th className="text-left p-3">Amount</th>
                      <th className="text-left p-3">Status</th>
                      <th className="text-left p-3">Date</th>
                      <th className="text-left p-3">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {transactions.map((transaction) => (
                      <tr 
                        key={transaction.id} 
                        className="border-b hover:bg-gray-50"
                        data-testid={`transaction-row-${transaction.id}`}
                      >
                        <td className="p-3">
                          <div>
                            <div className="font-medium">#{transaction.id}</div>
                            <div className="text-xs text-muted-foreground">
                              Qty: {transaction.quantity}
                              {transaction.unitsGranted && (
                                <> • Granted: {transaction.unitsGranted}</>
                              )}
                            </div>
                            <div className="text-xs text-muted-foreground">
                              Source: {transaction.source}
                            </div>
                          </div>
                        </td>
                        <td className="p-3">
                          <div>
                            <div className="font-medium">{transaction.userEmail}</div>
                            {(transaction.userFirstName || transaction.userLastName) && (
                              <div className="text-sm text-muted-foreground">
                                {transaction.userFirstName} {transaction.userLastName}
                              </div>
                            )}
                            <div className="text-xs text-muted-foreground">
                              ID: {transaction.userId}
                            </div>
                          </div>
                        </td>
                        <td className="p-3">
                          <div>
                            {transaction.addonDetails ? (
                              <>
                                <div className="font-medium text-sm">
                                  {transaction.addonDetails.displayName}
                                </div>
                                <div className="mt-1">
                                  {getAddonTypeBadge(transaction.addonDetails.type)}
                                </div>
                                <div className="text-xs text-muted-foreground mt-1">
                                  SKU: {transaction.addonDetails.sku}
                                </div>
                              </>
                            ) : (
                              <div className="text-sm text-muted-foreground">Unknown Add-on</div>
                            )}
                          </div>
                        </td>
                        <td className="p-3">
                          <div className="font-medium">
                            {formatCurrency(transaction.amountPaid, transaction.currency)}
                          </div>
                          <div className="text-xs text-muted-foreground uppercase">
                            {transaction.currency}
                          </div>
                        </td>
                        <td className="p-3">
                          {getStatusBadge(transaction.status)}
                        </td>
                        <td className="p-3">
                          <div className="text-sm">
                            {new Date(transaction.createdAt).toLocaleDateString()}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {new Date(transaction.createdAt).toLocaleTimeString()}
                          </div>
                        </td>
                        <td className="p-3">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => onSelectUser?.(transaction.userId)}
                            data-testid={`view-user-${transaction.userId}`}
                          >
                            View User
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {pagination && (
                <div className="flex items-center justify-between mt-6">
                  <div className="text-sm text-muted-foreground">
                    Showing {pagination.offset + 1} to {Math.min(pagination.offset + pagination.limit, pagination.total)} of {pagination.total} transactions
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={currentPage === 0}
                      onClick={() => setCurrentPage(currentPage - 1)}
                      data-testid="previous-page-button"
                    >
                      Previous
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={!pagination.hasMore}
                      onClick={() => setCurrentPage(currentPage + 1)}
                      data-testid="next-page-button"
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}